import React, {FunctionComponent, useEffect, useState} from "react";
import {makeStyles} from "tss-react/mui";
import {CircularProgress, Container, Typography} from "@mui/material";
import {
    CLEAR_DATA,
    CLEAR_FETCHING_DATA_FAILED,
    DISPOSITION_INITIALIZE_SAGA,
    FETCHING_DATA_START,
} from "@/Redux/Disposition/Constants";
import DispositionLoadingErrorModal from "@/Domains/DispositionManagement/Components/Modals/DispositionLoadingErrorModal";
import DispositionControlsMain from "@/Domains/DispositionManagement/Components/DispositionControls/DispositionControlsMain";
import DispositionBody from "@/Domains/DispositionManagement/Components/DispositionBody/DispositionBody";
import DispositionDateHeaderMain from "@/Domains/DispositionManagement/Components/DispositionDateHeader/DispositionDateHeaderMain";
import DispositionHeaderMain from "@/Domains/DispositionManagement/Components/DispositionHeader/DispositionHeaderMain";
import BookingInfoModal from "@/Domains/DispositionManagement/Components/Modals/BookingInfoModal";
import StationInfoModal from "@/Domains/DispositionManagement/Components/Modals/StationInfoModal";
import VehicleInfoModal from "@/Domains/DispositionManagement/Components/Modals/VehicleInfoModal";
import { useAppDispatch, useAppSelector } from "@/Redux/Redux";
import { useSelector } from "react-redux";
import {
    selectDispoDaysAfter,
    selectDispoDaysBefore,
    selectViewerDefaultRegion,
    selectViewerHomeStation,
    ViewerState,
} from "@/Redux/Auth/AuthReducer";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";
import moment from "moment";

interface DispositionProps {
    width: number | undefined;
    viewData: any;
    holidays: any;
}

const useStyles = makeStyles()(() => ({
    container: {
        "&.MuiContainer-root": {
            width: "100%",
            maxWidth: "100%",
            maxHeight: "100vh",
            padding: "6px",
            paddingTop: 0,
            paddingLeft: 0,
            paddingRight: 0,
            minHeight: "100vh",
        },
    },
    dispositionBody: {
        zIndex: 10,
        //width: "100%",
        transition: "width 0.5s, height 0.5s, opacity 0.5s 0.5s",
        overflow: "hidden",
    },
}));

const ManagerDispositionMainView: FunctionComponent<DispositionProps> = ({
                                                                             width,
                                                                             viewData,
                                                                             holidays,
                                                                         }): JSX.Element => {
    const {classes, cx} = useStyles();
    const {t} = useTranslation();
    const {
        fetchingDataIsFailed,
        initialized,
        isInitializing,
        isFetchingData,
        data,
        dispositionStartdate,
        dispositionEnddate,
        bookingInfoOverlayItem,
        vehicleInfoOverlayItem,
        stationInfoOverlayItem,
    } = useAppSelector((state) => state.disposition);
    //const viewer = useAppSelector(state => state.user.viewer)
    const dispoDaysBefore = useSelector((state) =>
        selectDispoDaysBefore(state as ViewerState)
    );
    const dispoDaysAfter = useSelector((state) =>
        selectDispoDaysAfter(state as ViewerState)
    );
    const homeStation = useSelector((state) =>
        selectViewerHomeStation(state as ViewerState)
    );
    const defaultRegion = useSelector((state) =>
        selectViewerDefaultRegion(state as ViewerState)
    );
    const [dispositionHeaderMainHeight, setDispositionHeaderMainHeight] =
        useState(0);
    const [dispositionControlsHeight, setDispositionControlsHeight] =
        useState(0);
    const [dispositionDateHeaderHeight, setDispositionDateHeaderHeight] =
        useState(0);
    const [dispositionControlsPositionTop, setDispositionControlsPositionTop] =
        useState(0);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!initialized && !isInitializing) {
            dispatch({
                type: DISPOSITION_INITIALIZE_SAGA,
                payload: {
                    dispoDaysBefore: dispoDaysBefore,
                    dispoDaysAfter: dispoDaysAfter,
                    selectedStation: homeStation,
                    defaultRegion: defaultRegion,
                },
            });
        }
        if (
            !fetchingDataIsFailed &&
            initialized &&
            !isFetchingData &&
            data === null
        ) {
            dispatch({
                type: FETCHING_DATA_START,
                payload: {
                    dispositionStartdate: dispositionStartdate,
                    dispositionEnddate: dispositionEnddate,
                },
            });
        }
    });

    useEffect(
        () => () => {
            dispatch({type: CLEAR_DATA});
        },
        []
    );

    const reduceDispoHeight =
        dispositionHeaderMainHeight +
        dispositionControlsHeight +
        dispositionDateHeaderHeight +
        "px";

    return (
        <>
            <Container fixed className={classes.container}>
                {bookingInfoOverlayItem && <BookingInfoModal
                    predefinedBookingId={bookingInfoOverlayItem?.bookingId}
                    predefinedStartdate={moment(dispositionEnddate).format(
                        "YYYY-MM-DDTHH:mm"
                    )}/>}
                {vehicleInfoOverlayItem && <VehicleInfoModal/>}
                {stationInfoOverlayItem && <StationInfoModal/>}
                {fetchingDataIsFailed && (
                    <DispositionLoadingErrorModal
                        open={fetchingDataIsFailed}
                        handleClose={() => {
                            dispatch({type: CLEAR_FETCHING_DATA_FAILED});
                        }}
                    />
                )}
                <DispositionHeaderMain
                    setHeight={setDispositionHeaderMainHeight}
                    contentWidth={width}
                />
                <DispositionControlsMain
                    setHeight={setDispositionControlsHeight}
                    setPositionTop={setDispositionControlsPositionTop}
                    contentWidth={width}
                />
                <DispositionDateHeaderMain
                    setHeight={setDispositionDateHeaderHeight}
                    dispositionControlsPositionTop={
                        dispositionControlsPositionTop
                    }
                    contentWidth={width}
                    startdate={dispositionStartdate}
                    dispoDaysAfter={dispoDaysAfter}
                    dispoDaysBefore={dispoDaysBefore}
                    viewData={viewData}
                    holidays={holidays}
                />

                {initialized && (
                    <>
                        <div
                            className={cx(
                                classes.dispositionBody,
                                "disposition__body"
                            )}
                            style={{
                                position: "relative",
                                top: reduceDispoHeight,
                                //bottom: 0,
                                height:
                                    "calc(100vh - " + reduceDispoHeight + ")", //dispositionBodyHeight,

                                //opacity: data !== null ? 1 : 0,
                                //background: 'red'
                            }}
                        >
                            {data !== null ? (
                                <DispositionBody contentWidth={width}/>
                            ) : (
                                <div
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        position: "absolute",
                                        width: "100%",
                                        height: "100%",
                                    }}
                                >
                                    <CircularProgress color="primary"/>
                                    <br/>
                                    <Typography component={"p"}>
                                        {t(
                                            "disposition.controls.loading.dataLoad"
                                        )}
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </Container>
        </>
    );
};

export default ManagerDispositionMainView;
